import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConflictingAppointment, ExceptionType } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useScheduleExceptionCreate } from './api';

const EXCEPTION_TYPES: ExceptionType[] = [
  'VACATION',
  'SICK_LEAVE',
  'TIME_OFF',
  'REPLACE',
  'EXTRA_DAY',
];

function todayIsoLocal(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

type Props = {
  userId: number;
  open: boolean;
  onClose: () => void;
};

export function ExceptionFormModal({ userId, open, onClose }: Props): ReactNode {
  const { t } = useTranslation();
  const create = useScheduleExceptionCreate(userId);

  const [type, setType] = useState<ExceptionType>('VACATION');
  const [date, setDate] = useState(todayIsoLocal());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<ConflictingAppointment[]>([]);

  useEffect(() => {
    if (open) {
      setType('VACATION');
      setDate(todayIsoLocal());
      setStartTime('09:00');
      setEndTime('17:00');
      setReason('');
      setErrorMsg(null);
      setConflicts([]);
    }
  }, [open, userId]);

  if (!open) return null;

  const needsTime = type === 'REPLACE' || type === 'EXTRA_DAY';

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setConflicts([]);

    try {
      await create.mutateAsync({
        userId,
        date,
        type,
        startTime: needsTime ? startTime : undefined,
        endTime: needsTime ? endTime : undefined,
        reason: reason.trim() || undefined,
      });
      onClose();
    } catch (err) {
      const detail = extractConflictDetail(err);
      if (detail) {
        setConflicts(detail);
      }
      setErrorMsg(extractApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl ring-1 ring-zinc-200">
        <header className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-zinc-900">
            {t('schedules.exception.formTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
            aria-label="close"
          >
            ✕
          </button>
        </header>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('schedules.exception.type')}
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ExceptionType)}
              className="input w-full"
            >
              {EXCEPTION_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`schedules.exception.types.${tp}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('schedules.exception.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="input w-full"
            />
          </div>

          {needsTime && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-zinc-700">
                  {t('schedules.exception.startTime')}
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  className="input w-full"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-zinc-700">
                  {t('schedules.exception.endTime')}
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  className="input w-full"
                />
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('schedules.exception.reason')}
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder={t('schedules.exception.reasonPlaceholder')}
              className="input w-full"
            />
          </div>

          {errorMsg && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {errorMsg}
              {conflicts.length > 0 && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-red-700">
                  {conflicts.map((c) => (
                    <li key={c.id}>
                      {formatLocalDateTime(c.startAt)} – {formatLocalTime(c.endAt)} ·{' '}
                      <span className="font-medium">{c.clientName}</span>{' '}
                      <span className="text-red-500">({c.status})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={create.isPending}>
              {create.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function extractConflictDetail(err: unknown): ConflictingAppointment[] | null {
  // axios error → response.data.error.details.appointments
  const e = err as {
    response?: { data?: { error?: { details?: { appointments?: ConflictingAppointment[] } } } };
  };
  return e?.response?.data?.error?.details?.appointments ?? null;
}

function formatLocalDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
}
function formatLocalTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}
